/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import gql from 'graphql-tag';

import Imgix from 'js/components/Imgix';

import { Link, Typography2, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import withFragments from 'bundles/graphql/components/withFragments';
import { getFullDisplayName, getLink } from 'bundles/graphql/utils/InstructorsV1';
import type { PartnerPageQuery_PartnersV1Resource_shortName_partners_instructors_elements as InstructorType } from 'bundles/partners-graphql/components/__generated__/PartnerPageQuery';
import constants from 'bundles/partners-graphql/constants';

const styles = {
  instructorContainer: css`
    min-height: 200px;
    width: 50%;
    float: left;
    max-width: 500px;
    padding-bottom: 15px;

    @media (max-width: 768px) {
      width: 100%;
      text-align: center;
      margin: 10px;
    }
  `,
  instructorDetails: css`
    padding: 0 40px 0 10px;

    @media (max-width: 768px) {
      padding: 0;
      text-align: left;
    }
  `,
  instructorName: css`
    margin-bottom: 10px;
  `,
  instructorPhoto: (theme: Theme) => css`
    img {
      background-color: var(--cds-color-white-0);
      border: 4px solid var(--cds-color-white-0);
      width: 100%;
      height: auto;
      box-shadow: 0 0 2px 0 rgb(0 0 0 / 26%);
      border-radius: 150px;
      display: block;
      background-position: center center;
      background-size: 100%;
      background-repeat: no-repeat;
    }
  `,
};

type Props = {
  instructor: InstructorType;
};

const Instructor = ({ instructor }: Props) => {
  const theme = useTheme();
  return (
    <div css={styles.instructorContainer} className="rc-Instructor bt3-row">
      <div css={styles.instructorPhoto(theme)} className="bt3-col-xs-4" data-e2e="instructor-photo">
        <Link href={getLink(instructor)}>
          <Imgix
            src={instructor.photo ? instructor.photo : constants.noheadshotUrl}
            alt={getFullDisplayName(instructor)}
            maxWidth={150}
            maxHeight={150}
            imgParams={{ fit: 'crop' }}
          />
        </Link>
      </div>
      <div css={styles.instructorDetails} className="bt3-col-xs-7" data-e2e="instructor-details">
        <Typography2 css={styles.instructorName} component="h4" variant="bodyPrimary">
          <Link variant="standard" href={getLink(instructor)} data-e2e="instructor-link">
            {getFullDisplayName(instructor)}
          </Link>
        </Typography2>
        <Typography2 component="div" variant="bodySecondary">
          {instructor.title}
        </Typography2>
        <Typography2 component="div" variant="bodySecondary">
          {instructor.department}
        </Typography2>
      </div>
      <div className="bt3-col-xs-1" />
    </div>
  );
};

export const instructorFragment = gql`
  fragment InstructorFragment on InstructorsV1 {
    photo
    title
    department
    ...InstructorsV1_getFullDisplayName
    ...InstructorsV1_getLink
  }
  ${getLink.fragment}
  ${getFullDisplayName.fragment}
`;

export default withFragments({
  instructorFragment,
})(Instructor);
